import { ShieldCheck, TrendingUp } from "lucide-react";

/**
 * LEOVerifiedChip — compact LEO earnings chip for degree cards.
 *
 * Two rendering modes:
 *   1. Progression mode (preferred): both `salary_3y.p50` and
 *      `salary_5y.p50` present → renders "£38k → £62k · 3–5y +63%"
 *      so a family sees the career trajectory at a glance.
 *   2. Legacy 5-year-only fallback: preserves the original chip so
 *      any degree that only has `salary_5y` in the LEO seed still
 *      renders sensibly.
 *
 * Clicks propagate to the parent Link so the whole card stays
 * clickable — the chip is decorative-only, not a nav target.
 */
export default function LEOVerifiedChip({ leo, testid }) {
  const p5 = leo?.salary_5y?.p50;
  const p3 = leo?.salary_3y?.p50;
  if (!p5 && !p3) return null;

  const kfmt = (v) => `£${Math.round(v / 1000)}k`;
  const showProgression = p3 && p5 && p5 > p3;
  const growthPct = showProgression
    ? Math.round(((p5 - p3) / p3) * 100)
    : null;

  const title = showProgression
    ? `LEO-verified · £${p3.toLocaleString()} at 3 years, £${p5.toLocaleString()} at 5 years post-graduation. HMRC tax-data-linked (${leo?.tax_year || "latest"} cohort).`
    : `LEO-verified · £${(p5 || p3).toLocaleString()} median ${p5 ? 5 : 3} years post-graduation. HMRC tax-data-linked (${leo?.tax_year || "latest"} cohort).`;

  return (
    <span
      data-testid={testid || "leo-verified-chip"}
      title={title}
      className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-900 text-[11px] px-2 py-0.5"
    >
      {showProgression ? (
        <TrendingUp className="h-3 w-3 text-emerald-700" aria-hidden />
      ) : (
        <ShieldCheck className="h-3 w-3 text-emerald-700" aria-hidden />
      )}
      {showProgression ? (
        <>
          <span className="font-medium">{kfmt(p3)}</span>
          <span className="text-emerald-700/70" aria-hidden>→</span>
          <span className="font-medium">{kfmt(p5)}</span>
          <span
            data-testid={`${testid || "leo-verified-chip"}-growth`}
            className="text-emerald-700 font-mono text-[10px] tracking-wider ml-0.5"
          >
            +{growthPct}%
          </span>
        </>
      ) : (
        <>
          <span className="font-medium">{kfmt(p5 || p3)}</span>
          <span className="text-emerald-700 font-underground-sc tracking-wider text-[9px]">
            LEO · {p5 ? "5" : "3"}yr
          </span>
        </>
      )}
    </span>
  );
}
